import React, { useEffect, useState } from "react";

import _ from "lodash";
import cx from "classnames";
import styles from "./audioAnalytics.module.scss";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";

interface AudioAnalyticsProps {
  analytics: {
    views: number;
    downloads: number;
    avgTimeSpent: number;
    shares: number;
    type: string;
    pageCount: number;
    dataPoints: {
      totalTimeSpent: number;
      count: number;
      avgTimeSpent: number;
      index: number;
    }[];
  };
  metadata: any;
  width: number;
  height: number;
}

const AudioAnalytics = ({ analytics, metadata, width, height }: AudioAnalyticsProps) => {
  const [segments, setSegments] = useState<
    {
      index: number;
      totalTimeSpent: number;
      count: number;
      avgTimeSpent?: number;
    }[]
  >([]);
  const [chart, setChart] = useState({
    width: width,
    height: height,
    visible: false,
  });

  const [activeIndex, setActiveIndex] = useState(0);
  const [seekTime, setSeekTime] = useState(0);
  const [activeSegment, setActiveSegment] = useState(null);
  const [duration, setDuration] = useState(0);
  const [segmentLength, setSegmentLength] = useState(0);

  const color = "#dbdbdb";
  const subTextColor = "#dbdbdb";
  const rootElement = document.querySelector(':root');
  const primaryColor = rootElement ? getComputedStyle(rootElement).getPropertyValue('--primaryColor') : '';

  const padNumber = (number = 0, pad: number) => {
    number = Math.ceil(number);

    const length = pad - String(number).length + 1;
    let padding = "";
    if (length > 0) {
      padding = Array(pad - String(number).length + 1).join("0");
    }
    return padding + number;
  };

  const formatTimeSpent = (timeSpent: number) => {
    const minutes = padNumber(Math.floor(timeSpent / 60), 2);
    const seconds = padNumber(timeSpent % 60, 2);

    return `${minutes}:${seconds}`;
  };

  const handleChartMouseOut = () => {
    setActiveIndex(0);
    setSeekTime(0);
    setActiveSegment(null);
  };

  const handleChartMouseMove = (data: any, index: number) => {
    if (!(data.activeCoordinate && data.activePayload)) {
      return;
    }

    let seekTime = data.activeCoordinate.x / chart.width;
    const totalDuration = duration;
    seekTime = seekTime * totalDuration;

    setActiveIndex(index);
    setSeekTime(seekTime);
    setActiveSegment(data.activePayload[0].payload);
  };

  const calculatePosition = (y) => {    
    if(chart.height < 100) {
      return chart.height - 20;
    } else if(chart.height - y > chart.height / 2) {
      return y;
    } else {
      return chart.height - y;
    }
  };

  const processAnalytics = () => {
    if (!analytics || analytics.dataPoints.length == 0) {
      return [];
    }
    
    const segmentLength = _.get(metadata, "audio_segment_length") || 5;
    let totalDuration = _.get(metadata, "video_duration") || 0;


    if (totalDuration == 0) {
      const lastSegment = analytics.dataPoints[analytics.dataPoints.length - 1];
      totalDuration = lastSegment.index + segmentLength;
    }

    let segmentCounts = Math.ceil(totalDuration / segmentLength);
    segmentCounts = Math.max(segmentCounts, analytics.dataPoints.length);

    const segmentsArr: { index: number; totalTimeSpent: number; count: number }[] = [];
    for (let i = 0; i < segmentCounts; i++) {
      const segment = {
        index: i * segmentLength,
        totalTimeSpent: 0.0,
        count: 0,
      };

      segmentsArr.push(segment);
    }

    analytics.dataPoints.forEach((element) => {
      const index = segmentsArr.findIndex((item: any) => {
        return item.index == element.index;
      });

      if (index != -1) {
        segmentsArr[index] = element;
      }
    });

    return {
      segmentsArr: segmentsArr,
      segmentLength: segmentLength,
      duration: totalDuration,
    };
  };

  useEffect(() => {
    if (analytics && analytics.dataPoints) {
      const processedData:any = processAnalytics();

      setSegments(processedData.segmentsArr);
      setDuration(processedData.duration);
      setSegmentLength(processedData.segmentLength);
    }
  }, []);
  
  useEffect(() => {
    if (analytics && analytics.dataPoints) {
      const processedData:any = processAnalytics();

      setSegments(processedData.segmentsArr);
      setDuration(processedData.duration);
      setSegmentLength(processedData.segmentLength);
    }  
  }, [analytics]);


  return (
    <ResponsiveContainer height={chart.height}>
    <div className={styles.assetViewChart}>
      <AreaChart
        width={chart.width}
        height={chart.height}
        data={segments}
        onMouseMove={handleChartMouseMove}
        onMouseLeave={handleChartMouseOut}
      >
        <XAxis
          dataKey="index"
          minTickGap={30}
          interval={"preserveStartEnd"}
          tickFormatter={formatTimeSpent}
          tickLine={false}
          axisLine={false}
          tick={{ fill: '#000' }}
          tickMargin={10}
        />
        <defs>
          <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={subTextColor} stopOpacity={0.8} />
            <stop offset="95%" stopColor={subTextColor} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area
          type="natural"
          dataKey="count"
          baseValue={0}
          min={0}
          fill={`url("#colorUv")`}
          stroke={color}
          fillOpacity={1}
          strokeWidth={1}
        ></Area>

        <Tooltip
          cursor={{
            fill: primaryColor,
            stroke: primaryColor,
          }}
          content={(props) => {
            // eslint-disable-next-line react/prop-types
            const {y} = props.coordinate || {
              y: 0
            };
            return (
              <div style={{
                position: "absolute",
                top: `-${calculatePosition(y)}px`,
              }}>
              <div className={cx(styles.tooltip)}>
                <div className={styles.tooltipContent}>
                  <span className={cx(styles.content, styles.views)}>
                    Views :{" "}
                    <strong>{`${activeSegment ? padNumber(_.get(activeSegment, "count"), 2) : 0}`}</strong>
                  </span>
                  <span className={styles.content}>
                    Time : <strong>{`${formatTimeSpent(seekTime)}`}</strong>
                  </span>
                </div>
              </div>
              </div>
            );
          }}
        />
      </AreaChart>
    </div>
    </ResponsiveContainer>
  );
};

export default AudioAnalytics;
